import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, ExternalLink, MessageSquare, Plus, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const READONLY_NOTE = { relation: true, files: true, formula: true, rollup: true, created_time: true, last_edited_time: true };

function Chip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-brand-ink/5 text-brand-ink rounded-full px-2.5 py-1">
      {label}{onRemove && <button onClick={onRemove} className="text-brand-tertiary hover:text-rose-600"><X className="h-3 w-3" /></button>}
    </span>
  );
}

function OfficeField({ label, value, options, onChange }) {
  const ids = Array.isArray(value) ? value : (value?.ids || []);
  const byId = Object.fromEntries((options || []).map((o) => [o.id, o.name]));
  const sel = new Set(ids);
  return <div data-testid="task-field-office">{label}
    <div className="flex flex-wrap gap-1.5 mb-2">
      {ids.map((i) => <Chip key={i} label={byId[i] || "Unknown office"} onRemove={() => onChange(ids.filter((x) => x !== i))} />)}
      {!ids.length && <span className="text-xs text-brand-tertiary">No location</span>}
    </div>
    <select value="" data-testid="task-office-add" onChange={(e) => e.target.value && onChange([...ids, e.target.value])}
      className="w-full text-sm border border-brand-border rounded-lg px-3 py-2 bg-brand-surface text-brand-ink outline-none">
      <option value="">+ Add location…</option>
      {(options || []).filter((o) => !sel.has(o.id)).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select></div>;
}

function Field({ field, value, users, onChange }) {
  const { name, type, options, editable } = field;
  const label = <label className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary block mb-1">{name}</label>;
  const inputCls = "w-full text-sm border border-brand-border rounded-lg px-3 py-2 bg-brand-surface text-brand-ink outline-none";

  if (field.office) return <OfficeField label={label} value={value} options={options} onChange={onChange} />;

  if (!editable || READONLY_NOTE[type]) {
    let display = "—";
    if (type === "relation") display = value?.count ? `${value.count} linked` : "—";
    else if (type === "files") display = (value || []).length ? `${value.length} file(s)` : "—";
    else if (value != null && value !== "") display = String(value);
    return <div data-testid={`task-field-${name}`}>{label}<p className="text-sm text-brand-muted">{display} <span className="text-[10px]">(read-only)</span></p></div>;
  }

  if (type === "title" || type === "rich_text")
    return <div data-testid={`task-field-${name}`}>{label}<textarea rows={type === "title" ? 2 : 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls + " resize-none"} /></div>;

  if (type === "select" || type === "status")
    return <div data-testid={`task-field-${name}`}>{label}
      <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={inputCls}>
        {type === "select" && <option value="">— None —</option>}
        {(options || []).map((o) => <option key={o.name} value={o.name}>{o.name}</option>)}
      </select></div>;

  if (type === "multi_select") {
    const set = new Set(value || []);
    return <div data-testid={`task-field-${name}`}>{label}
      <div className="flex flex-wrap gap-1.5">
        {(options || []).map((o) => {
          const on = set.has(o.name);
          return <button key={o.name} onClick={() => { const n = new Set(set); on ? n.delete(o.name) : n.add(o.name); onChange([...n]); }}
            className={`text-xs rounded-full px-2.5 py-1 border ${on ? "bg-brand-ink text-brand-page border-brand-ink" : "border-brand-border text-brand-muted"}`}>{o.name}</button>;
        })}
      </div></div>;
  }

  if (type === "people") {
    const selected = value || [];
    const selIds = new Set(selected.map((p) => p.id));
    return <div data-testid={`task-field-${name}`}>{label}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map((p) => <Chip key={p.id} label={p.name} onRemove={() => onChange(selected.filter((x) => x.id !== p.id))} />)}
        {!selected.length && <span className="text-xs text-brand-tertiary">Unassigned</span>}
      </div>
      <select value="" onChange={(e) => { const u = users.find((x) => x.id === e.target.value); if (u) onChange([...selected, { id: u.id, name: u.name }]); }} className={inputCls}>
        <option value="">+ Add person…</option>
        {users.filter((u) => !selIds.has(u.id)).map((u) => <option key={u.id} value={u.id}>{u.name}{u.guest ? " (guest)" : u.hub ? " (hub team)" : ""}</option>)}
      </select></div>;
  }

  if (type === "date") {
    const v = value || {};
    return <div data-testid={`task-field-${name}`}>{label}
      <input type="date" data-testid={`task-date-${name}`} value={(v.start || "").slice(0, 10)} onChange={(e) => onChange({ start: e.target.value || null, end: null })} className={inputCls} />
      </div>;
  }

  if (type === "number")
    return <div data-testid={`task-field-${name}`}>{label}<input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} /></div>;

  if (type === "checkbox")
    return <div data-testid={`task-field-${name}`} className="flex items-center gap-2 pt-4"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /><span className="text-sm text-brand-ink">{name}</span></div>;

  return <div data-testid={`task-field-${name}`}>{label}<input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} /></div>;
}

const TOP_SLOTS = [
  (f) => /priority/i.test(f.name) && ["select", "status"].includes(f.type),
  (f) => f.type === "status" || /^s(t)?atus$/i.test(f.name.trim()),
  (f) => f.office,
  (f) => f.type === "people" && /assign|in charge/i.test(f.name),
  (f) => f.type === "date" && /due/i.test(f.name),
  (f) => f.type === "rich_text" && /notes|description/i.test(f.name),
];

function splitFields(fields) {
  const used = new Set();
  const pick = (test) => { const f = fields.find((x) => !used.has(x.name) && test(x)); if (f) used.add(f.name); return f; };
  const title = pick((f) => f.type === "title");
  const top = TOP_SLOTS.map(pick).filter(Boolean);
  if (!top.some((f) => f.type === "people")) { const p = pick((f) => f.type === "people" && !/manager/i.test(f.name)); if (p) top.splice(Math.min(3, top.length), 0, p); }
  if (!top.some((f) => f.type === "date")) { const d = pick((f) => f.type === "date"); if (d) top.splice(Math.min(4, top.length), 0, d); }
  return { title, top, rest: fields.filter((f) => !used.has(f.name)) };
}

export default function TaskDetail({ pageId, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([
        api.get(`/notion/tasks/${pageId}/detail`),
        api.get(`/notion/users`),
      ]);
      setDetail(d.data);
      setUsers(u.data.users || []);
      setValues(Object.fromEntries(d.data.fields.map((f) => [f.name, f.value])));
      setDirty(new Set());
    } catch (e) { toast.error("Could not load task", { description: e.userMessage }); }
    finally { setLoading(false); }
  }, [pageId]);

  useEffect(() => { load(); }, [load]);

  const setField = (name, val) => { setValues((v) => ({ ...v, [name]: val })); setDirty((d) => new Set(d).add(name)); };

  const titleField = detail?.fields.find((f) => f.type === "title");
  const split = detail ? splitFields(detail.fields) : null;
  const cell = (f) => (
    <div key={f.name} className={f.type === "title" || f.type === "rich_text" || f.office || f.type === "people" ? "sm:col-span-2" : ""}>
      <Field field={f} value={values[f.name]} users={users} onChange={(val) => setField(f.name, val)} />
    </div>
  );

  const save = async () => {
    const changed = detail.fields.filter((f) => dirty.has(f.name) && f.editable &&
      JSON.stringify(values[f.name]) !== JSON.stringify(f.value));
    if (!changed.length) { onClose(); return; }
    setSaving(true);
    try {
      const updates = changed.map((f) => ({ name: f.name, type: f.type, value: values[f.name] }));
      await api.patch(`/notion/tasks/${pageId}/props`, { updates });
      toast.success("Task saved");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const tryClose = () => {
    if (dirty.size && !window.confirm("Discard unsaved changes?")) return;
    onClose();
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/notion/tasks/${pageId}/comments`, { text: comment.trim() }); setComment(""); toast.success("Comment added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const postNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/notion/tasks/${pageId}/body`, { text: note.trim() }); setNote(""); toast.success("Added to description"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this task in Notion?")) return;
    try { await api.delete(`/notion/tasks/${pageId}`); toast.success("Task archived"); onChanged?.(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={tryClose} data-testid="task-detail">
      <div className="bg-brand-page w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-brand-page border-b border-brand-border px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-serif text-xl text-brand-ink truncate pr-3">{titleField ? (values[titleField.name] || "Untitled") : "Task"}</h3>
          <div className="flex items-center gap-3 shrink-0">
            {detail?.url && <a href={detail.url} target="_blank" rel="noreferrer" className="text-brand-tertiary hover:text-brand-ink" title="Open in Notion"><ExternalLink className="h-4 w-4" /></a>}
            <button onClick={tryClose} className="text-brand-muted hover:text-brand-ink"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded-lg bg-brand-surface border border-brand-border animate-pulse" />)}</div>
        ) : detail ? (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="task-top-fields">
              {split.title && cell(split.title)}
              {split.top.map(cell)}
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary mb-2">Description</p>
              <div className="rounded-lg border border-brand-border bg-brand-surface p-3 space-y-1.5 max-h-56 overflow-y-auto" data-testid="task-body">
                {detail.body.length ? detail.body.map((b, i) => (
                  <p key={i} className={`text-sm ${b.type.startsWith("heading") ? "font-medium text-brand-ink" : "text-brand-muted"}`}>
                    {b.type === "to_do" ? (b.checked ? "☑ " : "☐ ") : (b.type.includes("list_item") ? "• " : "")}{b.text || "\u00A0"}
                  </p>
                )) : <p className="text-sm text-brand-tertiary italic">No description yet.</p>}
              </div>
              <div className="flex gap-2 mt-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postNote()}
                  placeholder="Add a line to the description…" data-testid="task-body-input"
                  className="flex-1 text-sm border border-brand-border rounded-lg px-3 py-2 bg-brand-surface text-brand-ink outline-none" />
                <button onClick={postNote} data-testid="task-body-add" className="px-3 rounded-lg border border-brand-border text-brand-muted hover:text-brand-ink"><Plus className="h-4 w-4" /></button>
              </div>
            </div>

            {split.rest.length > 0 && (
              <div data-testid="task-more-fields">
                <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary mb-3 border-t border-brand-border pt-4">More details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{split.rest.map(cell)}</div>
              </div>
            )}

            <div>
              <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary mb-2 flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Comments ({detail.comments.length})</p>
              <div className="space-y-3 mb-3" data-testid="task-comments">
                {detail.comments.map((c) => (
                  <div key={c.id} className="text-sm">
                    <span className="text-brand-ink font-medium">{c.author}</span>
                    <span className="text-[10px] text-brand-tertiary ml-2">{c.created_time ? new Date(c.created_time).toLocaleString() : ""}</span>
                    <p className="text-brand-muted whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))}
                {!detail.comments.length && <p className="text-sm text-brand-tertiary italic">No comments yet.</p>}
              </div>
              <div className="flex gap-2">
                <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postComment()}
                  placeholder="Write a comment…" data-testid="task-comment-input"
                  className="flex-1 text-sm border border-brand-border rounded-lg px-3 py-2 bg-brand-surface text-brand-ink outline-none" />
                <button onClick={postComment} data-testid="task-comment-send" className="px-3 rounded-lg bg-brand-ink text-brand-page"><Send className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ) : null}

        {detail && !loading && (
          <div className="sticky bottom-0 bg-brand-page border-t border-brand-border px-6 py-3 flex items-center justify-between">
            <button onClick={archive} data-testid="task-archive" className="text-sm text-rose-600 hover:text-rose-700">Archive</button>
            <div className="flex items-center gap-3">
              <button onClick={tryClose} className="text-sm text-brand-muted">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="task-save"
                className="bg-brand-ink text-brand-page text-sm rounded-lg px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : dirty.size ? `Save ${dirty.size} change${dirty.size > 1 ? "s" : ""}` : "Done"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
